import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { challenges as challengesApi, referrals as referralsApi, wallet } from '@/api/endpoints'
import { useWalletStore } from '@/store/walletStore'
import type { Challenge, MyReferralEntry, MyCodeData, ChallengeProgress } from '@/types'
import { ShareSheet } from '@/components/ShareSheet/ShareSheet'
import { useMiniToast } from '@/components/MiniToast/MiniToast'
import styles from './RewardsPage.module.css'

// ── Helpers ───────────────────────────────────────────────────────────────────

function rewardLabel(reward: Challenge['reward']) {
  switch (reward.type) {
    case 'bonus_credit':
    case 'coins':        return `${reward.amount} Bonus Coins`
    case 'deposit_credit':
    case 'cash':         return `₦${Number(reward.amount).toLocaleString()} cash`
    case 'free_spins':   return `${reward.amount} spin${reward.amount > 1 ? 's' : ''}`
    case 'multiplier_boost': return `${reward.amount}×`
    default:             return `${reward.amount}`
  }
}

function rewardBadge(reward: Challenge['reward']) {
  switch (reward.type) {
    case 'bonus_credit':
    case 'coins':        return `🎁${reward.amount}`
    case 'deposit_credit':
    case 'cash':         return `₦${Number(reward.amount).toLocaleString()}`
    case 'free_spins':   return `${reward.amount} spin${reward.amount > 1 ? 's' : ''}`
    case 'multiplier_boost': return `${reward.amount}×`
    default:             return `${reward.amount}`
  }
}

/** Claimable first → in-progress → not-started → claimed */
function challengeSortScore(p: ChallengeProgress | null): number {
  if (p?.claimable)      return 0
  if (p && !p.is_completed) return 1
  if (!p)                return 2
  return 3 // reward_claimed
}

function challengeIcon(type: string) {
  switch (type) {
    case 'welcome': return '🎡'
    case 'spin_count':
    case 'spin_streak': return '🌀'
    case 'daily_login':
    case 'login_streak': return '📅'
    case 'referral': return '👥'
    case 'deposit':
    case 'deposit_streak': return '💰'
    case 'win_streak': return '🏆'
    case 'total_staked': return '📊'
    default: return '⭐'
  }
}

function challengeProgressText(challenge: Challenge) {
  const p = challenge.my_progress
  const target = challenge.criteria.target_count as number

  // total_staked uses Naira amounts, not counts
  if (challenge.type === 'total_staked') {
    const current = p?.current_count ?? 0
    return `₦${current.toLocaleString()} / ₦${target.toLocaleString()} staked`
  }

  if (!p) return `0 / ${target}`
  return `${p.current_count} / ${target}`
}

function referralStatusLabel(status: MyReferralEntry['status']) {
  switch (status) {
    case 'pending': return 'Waiting for deposit'
    case 'qualified': return 'Reward incoming…'
    case 'rewarded': return 'Reward received'
    case 'rejected': return 'Rejected'
    default: return status
  }
}

// ── Challenge Card ────────────────────────────────────────────────────────────

function ChallengeCard({
  challenge,
  onWelcomeSpin,
  onClaim,
  claiming,
}: {
  challenge: Challenge
  onWelcomeSpin: () => void
  onClaim: (id: string) => void
  claiming: boolean
}) {
  const p = challenge.my_progress
  const target = (challenge.criteria.target_count as number) ?? 1
  const current = p?.current_count ?? 0
  const pct = Math.min(p?.progress_pct ?? 0, 100)
  const isCompleted = p?.is_completed ?? false
  const isClaimable = p?.claimable ?? false
  const rewardClaimed = p?.reward_claimed ?? false

  // ── Welcome spin — no progress bar, just a CTA ──
  if (challenge.type === 'welcome') {
    return (
      <div className={styles.card}>
        <p className={styles.cardTitle}>{challenge.name}</p>
        <div className={styles.cardRow}>
          <div className={styles.cardIcon}>🎡</div>
          <div className={styles.cardMeta}>
            <p className={styles.cardDescription}>
              {isCompleted ? 'Welcome spin used!' : 'You have one free spin!'}
            </p>
          </div>
          {!isCompleted && (
            <button className={styles.btn} onClick={onWelcomeSpin}>
              Spin Now
            </button>
          )}
          {isCompleted && (
            <span className={`${styles.btn} ${styles.btnDone}`}>Done ✓</span>
          )}
        </div>
      </div>
    )
  }

  // ── Referral — rendered separately ──
  if (challenge.type === 'referral') return null

  // ── Standard progress challenges ──
  // Button state hierarchy:
  //   claimable   → glowing "Claim {amount}!" (calls claim API)
  //   rewardClaimed / (completed & !claimable) → "Claimed ✓" (done)
  //   in progress → "In Progress" (disabled)
  const isDone = rewardClaimed || (isCompleted && !isClaimable)

  return (
    <div className={styles.card}>
      <p className={styles.cardTitle}>{challenge.name}</p>
      <div className={styles.cardRow}>
        <div className={styles.cardIcon}>
          {challengeIcon(challenge.type)}
          <span className={styles.coinBadge}>{rewardBadge(challenge.reward)}</span>
        </div>
        <div className={styles.cardMeta}>
          <p className={styles.cardDescription}>
            {challenge.type === 'daily_login' || challenge.type === 'login_streak'
              ? p
                ? 'Logged in today ✓'
                : 'Log in to earn'
              : challengeProgressText(challenge)}
          </p>
          <p className={styles.cardSub}>{challenge.description}</p>
        </div>

        {isClaimable ? (
          <button
            className={`${styles.btn} ${styles.btnClaim}`}
            disabled={claiming}
            onClick={() => onClaim(challenge.id)}
          >
            {claiming ? '…' : `Claim ${rewardLabel(challenge.reward)}!`}
          </button>
        ) : isDone ? (
          <span className={`${styles.btn} ${styles.btnDone}`}>Claimed ✓</span>
        ) : (
          <span className={`${styles.btn} ${styles.btnDisabled}`}>In Progress</span>
        )}
      </div>

      {/* Progress bar */}
      <div className={styles.progressWrap}>
        <div className={styles.progressBar}>
          <div
            className={`${styles.progressFill}${isCompleted || isDone ? ` ${styles.progressFillDone}` : ''}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        <p className={styles.progressText}>
          {challenge.type === 'total_staked'
            ? `₦${current.toLocaleString()} / ₦${target.toLocaleString()}`
            : `${current} / ${target}`}
        </p>
      </div>
    </div>
  )
}

// ── Referral Bonus Card ───────────────────────────────────────────────────────

function ReferralBonusCard({
  referralChallenge,
  referrals,
  onShare,
}: {
  referralChallenge: Challenge | null
  referrals: MyReferralEntry[]
  onShare: () => void
}) {
  if (!referralChallenge && referrals.length === 0) return null

  const reward = referralChallenge?.reward ?? { type: 'coins', amount: 500 }

  return (
    <div className={styles.card}>
      <p className={styles.cardTitle}>Referral Bonus</p>

      {referrals.length === 0 ? (
        <div className={styles.cardRow}>
          <div className={styles.cardIcon}>
            👥
            <span className={styles.coinBadge}>{rewardBadge(reward)}</span>
          </div>
          <div className={styles.cardMeta}>
            <p className={styles.cardDescription}>Invite friends to earn rewards</p>
            <p className={styles.cardSub}>Earn {rewardLabel(reward)} per referral</p>
          </div>
          <button className={styles.btn} onClick={onShare}>
            Invite
          </button>
        </div>
      ) : (
        <div className={styles.referralList}>
          {referrals.map((ref, i) => {
            const isRewarded = ref.status === 'rewarded'
            const isPending = ref.status === 'pending'
            return (
              <div key={ref.id}>
                {i > 0 && <div className={styles.divider} />}
                <div className={styles.cardRow} style={{ paddingTop: i > 0 ? 8 : 0 }}>
                  <div className={styles.cardIcon}>
                    👥
                    <span className={styles.coinBadge}>{rewardBadge(reward)}</span>
                  </div>
                  <div className={styles.cardMeta}>
                    <p className={styles.cardDescription}>{ref.referred_user_name}</p>
                    <p className={styles.cardSub}>{referralStatusLabel(ref.status)}</p>
                  </div>
                  <button
                    className={
                      isRewarded
                        ? `${styles.btn} ${styles.btnDone}`
                        : isPending
                          ? `${styles.btn} ${styles.btnDisabled}`
                          : styles.btn
                    }
                    disabled={isPending || isRewarded}
                  >
                    {isRewarded ? 'Claimed ✓' : 'Pending'}
                  </button>
                </div>
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ── RewardsPage ───────────────────────────────────────────────────────────────

export function RewardsPage() {
  const navigate = useNavigate()
  const toast = useMiniToast()
  const { setBalance } = useWalletStore()

  const [challengesList, setChallengesList] = useState<Challenge[]>([])
  const [referralList, setReferralList] = useState<MyReferralEntry[]>([])
  const [referralCode, setReferralCode] = useState<MyCodeData | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [shareSheetOpen, setShareSheetOpen] = useState(false)
  const [claimingId, setClaimingId] = useState<string | null>(null)

  const loadData = useCallback(async () => {
    try {
      const [challengesRes, referralsRes, codeRes] = await Promise.allSettled([
        challengesApi.list(),
        referralsApi.myReferrals(),
        referralsApi.myCode(),
      ])
      if (challengesRes.status === 'fulfilled') {
        setChallengesList(challengesRes.value.challenges ?? [])
      }
      if (referralsRes.status === 'fulfilled') {
        setReferralList(referralsRes.value.referrals ?? [])
      }
      if (codeRes.status === 'fulfilled') {
        setReferralCode(codeRes.value)
      }
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => { loadData() }, [loadData])

  const handleClaim = useCallback(async (challengeId: string) => {
    if (claimingId) return
    setClaimingId(challengeId)
    try {
      const res = await challengesApi.claim(challengeId)
      // Use credited_to_label for a specific toast e.g. "You earned 50 Bonus Coins!"
      const msg = res.credited_to_label
        ? `You earned ${res.amount} ${res.credited_to_label}!`
        : res.message ?? 'Reward claimed!'
      toast.show(msg, 'success')

      // Refresh challenges AND wallet balance in parallel
      const [updated, bal] = await Promise.all([
        challengesApi.list(),
        wallet.balance(),
      ])
      setChallengesList(updated.challenges ?? [])
      setBalance(bal)
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ??
        err?.response?.data?.error ??
        'Failed to claim reward.'
      toast.show(msg, 'error')
    } finally {
      setClaimingId(null)
    }
  }, [claimingId, toast, setBalance])

  function handleWelcomeSpin() {
    navigate('/')
  }

  function handleShare() {
    if (referralCode?.referral_code) {
      setShareSheetOpen(true)
    } else {
      navigate('/profile')
    }
  }

  if (isLoading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    )
  }

  const referralChallenge = challengesList.find((c) => c.type === 'referral') ?? null
  // Sort: claimable → in-progress → not-started → claimed
  const otherChallenges = [...challengesList]
    .filter((c) => c.type !== 'referral')
    .sort((a, b) => challengeSortScore(a.my_progress) - challengeSortScore(b.my_progress))

  return (
    <div className={styles.page}>
      <h1 className={styles.pageTitle}>Rewards</h1>

      {challengesList.length === 0 && referralList.length === 0 ? (
        <p className={styles.empty}>No rewards available right now.</p>
      ) : (
        <>
          {otherChallenges.map((ch) => (
            <ChallengeCard
              key={ch.id}
              challenge={ch}
              onWelcomeSpin={handleWelcomeSpin}
              onClaim={handleClaim}
              claiming={claimingId === ch.id}
            />
          ))}

          <ReferralBonusCard
            referralChallenge={referralChallenge}
            referrals={referralList}
            onShare={handleShare}
          />
        </>
      )}

      {shareSheetOpen && referralCode && (
        <ShareSheet
          data={{
            code: referralCode.referral_code,
            shareUrl: referralCode.share_url,
            text: `Join me on Spin Rewards and earn bonus coins! Use my referral code: ${referralCode.referral_code}`,
          }}
          onClose={() => setShareSheetOpen(false)}
        />
      )}

      <toast.View />
    </div>
  )
}
